import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { formatDate, formatNumber, titleCase } from '../../lib/format'
import type { Load } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Select, Skeleton,
} from '../../components/ui'
import { CloseIcon, PlusIcon, RouteIcon } from '../../components/icons'
import { Wizard } from '../../components/Wizard'
import { RangeChips, WeeklyBarChart, WeeklyLineChart, bucketWeekly } from '../../components/charts'

/* Platform-wide loads ledger - port of admin/loads.html + admin/log_load.html
 * (admin/routes.py loads_list()/load_log()). Unscoped by org, unlike the
 * leader portal's own Loads.tsx or the /ops/dispatch personal view - this is
 * super_admin's oversight of every haul on the platform. */

interface TruckOption { id?: string; plate?: string }
interface MineralSummary { tonnage: number; value: number; count: number }
interface LoadsResponse {
  ok: boolean
  items: Load[]
  summary: { count: number; total_tonnage: number; total_value: number; by_mineral: Record<string, MineralSummary> }
  trucks: TruckOption[]
}

export default function AdminLoads() {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  // Summary = tiles + charts; Table = filters + the raw ledger. Mirrors the
  // reference admin panel's Summary/Activity split so reading trends and
  // digging into rows are separate modes, not one crowded scroll.
  const [view, setView] = useState<'summary' | 'table'>('summary')
  const [weeks, setWeeks] = useState(8)
  const [truckFilter, setTruckFilter] = useState('')
  const [mineralFilter, setMineralFilter] = useState('')
  const [since, setSince] = useState('')
  const [until, setUntil] = useState('')

  const params = new URLSearchParams()
  if (truckFilter) params.set('truck_id', truckFilter)
  if (mineralFilter) params.set('mineral', mineralFilter)
  if (since) params.set('since', since)
  if (until) params.set('until', until)
  const qs = params.toString()

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'loads', qs],
    queryFn: () => api.get<LoadsResponse>(`/admin/loads${qs ? `?${qs}` : ''}`),
  })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post('/admin/loads', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'loads'] })
      setShowForm(false)
    },
  })

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    create.mutate({
      truck_id: String(form.get('truck_id') ?? ''),
      mineral_type: String(form.get('mineral_type') ?? '').trim(),
      tonnage: Number(form.get('tonnage') ?? 0),
      source_label: String(form.get('source_label') ?? '').trim(),
      destination_label: String(form.get('destination_label') ?? '').trim(),
      value_local: form.get('value_local') ? Number(form.get('value_local')) : null,
      currency: String(form.get('currency') ?? 'ZMW').trim() || 'ZMW',
      buyer_name: String(form.get('buyer_name') ?? '').trim(),
      notes: String(form.get('notes') ?? '').trim(),
    })
  }

  const loads = data?.items ?? []
  const summary = data?.summary
  const trucks = data?.trucks ?? []
  const minerals = summary ? Object.entries(summary.by_mineral) : []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Loads</h1>
          <p className="page-head__sub">Every haul recorded on the platform, across every organisation.</p>
        </div>
        <Button className="fab-trigger__inline" icon={<PlusIcon />} onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Cancel' : 'Log a load'}
        </Button>
        <button
          type="button" className="fab"
          onClick={() => setShowForm((s) => !s)}
          aria-label={showForm ? 'Cancel' : 'Log a load'}
        >
          {showForm ? <CloseIcon size={22} /> : <PlusIcon size={22} />}
        </button>
      </div>

      {showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="Log a load" />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                {create.isError && (
                  <Alert tone="danger">
                    {create.error instanceof ApiError ? create.error.message : 'Could not log that load.'}
                  </Alert>
                )}
                {/* PWA: 3 guided steps; desktop stacked, unchanged. */}
                <Wizard
                  steps={[
                    { title: 'Truck & cargo', content: (
                      <div className="stack">
                        <div className="grid grid--2">
                          <Select label="Truck" name="truck_id" required defaultValue="">
                            <option value="" disabled>Select a truck…</option>
                            {trucks.map((t) => (
                              <option key={t.id} value={t.id}>{t.plate || t.id}</option>
                            ))}
                          </Select>
                          <Input label="Mineral" name="mineral_type" required />
                        </div>
                        <Input label="Tonnage" name="tonnage" type="number" step="any" min="0" required />
                      </div>
                    ) },
                    { title: 'Route', content: (
                      <div className="grid grid--2">
                        <Input label="Loading from" name="source_label" placeholder="e.g. Pit 3" />
                        <Input label="Destination" name="destination_label" placeholder="e.g. Chambeshi smelter" />
                      </div>
                    ) },
                    { title: 'Sale', content: (
                      <div className="stack">
                        <div className="grid grid--2">
                          <Input label="Value (optional)" name="value_local" type="number" step="any" min="0" />
                          <Input label="Currency" name="currency" defaultValue="ZMW" />
                        </div>
                        <Input label="Buyer (optional)" name="buyer_name" />
                      </div>
                    ) },
                    { title: 'Notes', content: (
                      <Input label="Notes (optional)" name="notes" />
                    ) },
                  ]}
                  footer={(
                    <div className="row">
                      <Button type="submit" loading={create.isPending}>Log load</Button>
                    </div>
                  )}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <div className="tab-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
        <Button variant={view === 'summary' ? 'primary' : 'secondary'} size="sm" onClick={() => setView('summary')}>
          Summary
        </Button>
        <Button variant={view === 'table' ? 'primary' : 'secondary'} size="sm" onClick={() => setView('table')}>
          Table
        </Button>
      </div>

      {view === 'summary' && !isLoading && (
        <div className="stack" style={{ gap: 'var(--sp-5)', marginBottom: 'var(--sp-6)' }}>
          <RangeChips value={weeks} onChange={setWeeks} />
          <Card>
            <CardHeader title="Loads per week" sub={`Last ${weeks} weeks, all organisations`} />
            <CardBody>
              {(() => { const s = bucketWeekly(loads, (l) => l.load_time || l.created_at, undefined, weeks); return (
                <WeeklyLineChart weeks={s.weeks} series={[{ label: 'Loads', values: s.values }]} />
              ) })()}
            </CardBody>
          </Card>
          <Card>
            <CardHeader title="Tonnage per week" sub={`Total tonnes moved, last ${weeks} weeks`} />
            <CardBody>
              {(() => { const s = bucketWeekly(loads, (l) => l.load_time || l.created_at, (l) => Number(l.tonnage) || 0, weeks); return (
                <WeeklyBarChart weeks={s.weeks} values={s.values} unit=" t" />
              ) })()}
            </CardBody>
          </Card>
        </div>
      )}

      {view === 'table' && (
      <div className="filter-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
        <Select label="Truck" value={truckFilter} onChange={(e) => setTruckFilter(e.target.value)}>
          <option value="">All trucks</option>
          {trucks.map((t) => <option key={t.id} value={t.id}>{t.plate || t.id}</option>)}
        </Select>
        <Input label="Mineral" value={mineralFilter} onChange={(e) => setMineralFilter(e.target.value)} />
        <Input label="Since" type="date" value={since} onChange={(e) => setSince(e.target.value)} />
        <Input label="Until" type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
      </div>

      )}

      {view === 'summary' && summary && summary.count > 0 && (
        <div className="grid grid--tiles-3" style={{ gap: 'var(--sp-4)', marginBottom: 'var(--sp-6)' }}>
          <Card><CardBody><div className="stat"><div className="stat__label">Loads</div><div className="stat__value">{summary.count}</div></div></CardBody></Card>
          <Card><CardBody><div className="stat"><div className="stat__label">Total tonnage</div><div className="stat__value">{formatNumber(summary.total_tonnage, 1)} t</div></div></CardBody></Card>
          <Card><CardBody><div className="stat"><div className="stat__label">Total value</div><div className="stat__value">{formatNumber(summary.total_value, 2)}</div></div></CardBody></Card>
        </div>
      )}

      {view === 'summary' && minerals.length > 0 && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="By mineral" />
          <CardBody flush>
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Mineral</th><th className="table__num">Loads</th><th className="table__num">Tonnage</th><th className="table__num">Value</th></tr></thead>
                <tbody>
                  {minerals.map(([mineral, m]) => (
                    <tr key={mineral}>
                      <td style={{ fontWeight: 600 }}>{titleCase(mineral)}</td>
                      <td className="table__num">{m.count}</td>
                      <td className="table__num">{formatNumber(m.tonnage, 1)} t</td>
                      <td className="table__num">{formatNumber(m.value, 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}

      {view === 'table' && (
      <Card>
        <CardHeader title="Loads" />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack"><Skeleton h={20} /><Skeleton h={20} /></div>
          ) : loads.length === 0 ? (
            <EmptyState icon={<RouteIcon size={22} />} title="No loads found" body="Loads matching these filters will appear here." />
          ) : (
            <>
              {/* PWA tap rows - the 6-column ledger clips on a phone;
                  desktop keeps the table. */}
              <div className="mobile-only">
                {loads.map((l) => (
                  <div key={l.id} className="lrow">
                    <div className="lrow__main">
                      <div className="lrow__title">
                        {titleCase(l.mineral_type) || 'Load'} · {formatNumber(l.tonnage, 1)} t
                      </div>
                      <div className="lrow__sub">
                        {[[l.source_label, l.destination_label].filter(Boolean).join(' → '),
                          l.buyer_name, formatDate(l.load_time || l.created_at)]
                          .filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    {l.value_local != null && (
                      <div className="lrow__end" style={{ fontWeight: 700 }}>
                        {formatNumber(l.value_local, 0)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="table-wrap desktop-only">
                <table className="table">
                  <thead><tr><th>Date</th><th>Mineral</th><th className="table__num">Tonnage</th><th>Route</th><th>Buyer</th><th className="table__num">Value</th></tr></thead>
                  <tbody>
                    {loads.map((l) => (
                      <tr key={l.id}>
                        <td>{formatDate(l.load_time || l.created_at)}</td>
                        <td style={{ fontWeight: 600 }}>{titleCase(l.mineral_type) || 'Load'}</td>
                        <td className="table__num">{formatNumber(l.tonnage, 1)} t</td>
                        <td>{[l.source_label, l.destination_label].filter(Boolean).join(' → ') || '—'}</td>
                        <td>{l.buyer_name || '—'}</td>
                        <td className="table__num">{l.value_local != null ? formatNumber(l.value_local, 2) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardBody>
      </Card>
      )}
    </>
  )
}
